import { withForm } from '@components/ui/form/form';
import { FormDescription } from '@components/ui/form/shared.component';
import { Skeleton } from '@components/ui/skeleton';
import { useGetServices } from '@queries/service.query';
import { MILITARY_SERVICE_CODE_TO_COUNTRY, MILITARY_SERVICE_COUNTRY_TO_CODE, countries } from '@utils/constants';
import { useCallback } from 'react';
import { companyFormOptions } from './company-form-options';

const countryOptions = countries.map((country) => ({
  label: country.name,
  value: country.code,
}));

export const PreviousGovernmentWorkSection = withForm({
  ...companyFormOptions,
  render: function Render({ form }) {
    const { data: servicesData, isLoading: isLoadingServices } = useGetServices();

    const getMilitaryServicesForCountries = useCallback(
      (selectedCountryCodes: string[]) => {
        if (!servicesData || selectedCountryCodes.length === 0) {
          return [];
        }

        const countriesWithServices = selectedCountryCodes.filter(
          (countryCode) => countryCode in MILITARY_SERVICE_CODE_TO_COUNTRY,
        );
        if (countriesWithServices.length === 0) {
          return [];
        }

        return servicesData
          .filter((service) =>
            countriesWithServices.some((countryCode) => MILITARY_SERVICE_CODE_TO_COUNTRY[countryCode] === service.name),
          )
          .flatMap((country) =>
            country.subServices.map((service) => ({
              label: `${MILITARY_SERVICE_COUNTRY_TO_CODE[country.name] || country.name}: ${service.name}`,
              value: service.id,
            })),
          );
      },
      [servicesData],
    );

    return (
      <div className="flex flex-col gap-4">
        <form.AppField
          listeners={{
            onChange: ({ value }) => {
              const currentServices = form.getFieldValue('previousGovernmentWorkServiceIds') ?? [];
              const selectedCountryCodes = value ?? [];

              if (selectedCountryCodes.length === 0) {
                form.setFieldValue('previousGovernmentWorkServiceIds', []);
                return;
              }

              const validServiceIds = getMilitaryServicesForCountries(selectedCountryCodes).map(
                (service) => service.value,
              );
              const filteredServices = currentServices.filter((serviceId: string) =>
                validServiceIds.includes(serviceId),
              );

              if (filteredServices.length !== currentServices.length) {
                form.setFieldValue('previousGovernmentWorkServiceIds', filteredServices);
              }
            },
          }}
          mode="array"
          name="previousGovernmentWorkCountries"
        >
          {(countriesField) => (
            <div className="flex w-full flex-col gap-1.5">
              <countriesField.SelectMultipleField
                className="w-full"
                label="Previous Government Work"
                options={countryOptions}
                placeholder="Select countries..."
              />
              <FormDescription>
                This question helps us understand your experience delivering work for government-so we can gauge
                familiarity with relevant procurement, security, and compliance expectations.
              </FormDescription>
            </div>
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => state.values.previousGovernmentWorkCountries}>
          {(selectedCountries) => {
            const countryCodes = selectedCountries ?? [];
            const militaryOptions = getMilitaryServicesForCountries(countryCodes);

            if (countryCodes.length === 0 || militaryOptions.length === 0) {
              return null;
            }

            return isLoadingServices ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <form.AppField
                mode="array"
                name="previousGovernmentWorkServiceIds"
              >
                {(servicesField) => (
                  <servicesField.SelectMultipleField
                    className="w-full"
                    label="Military Services"
                    options={militaryOptions}
                    placeholder="Select military services..."
                  />
                )}
              </form.AppField>
            );
          }}
        </form.Subscribe>
      </div>
    );
  },
});
